import { i18n } from 'i18next';
import { List } from 'immutable';
import { ChatSession } from 'flow/types/Chats';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import {
    SummarizeOptionValue,
    SummaryOption,
} from 'flow/types/SummarizeMessage';
import { TypedMap } from 'flow/types/TypedMap';

export const isOnePersonChat = (
    chatSession: TypedMap<ChatSession>,
    chatPersonExtension: TypedMap<Record<ExtensionKey, TypedMap<Extension>>>,
) => {
    const memberSize = chatSession.get('members', List()).size;
    return (
        memberSize === 2 &&
        chatSession.get('roomType') === 'session' &&
        chatPersonExtension
    );
};

export const sessionIsNotArchived = (chatSession: TypedMap<ChatSession>) =>
    !sessionIsArchived(chatSession);

export const sessionIsArchived = (chatSession: TypedMap<ChatSession>) =>
    chatSession.get('chatArchived') !== undefined;

const isEmoji = (char: string) => {
    const code = char.codePointAt(0);
    if (!code) {
        return false;
    }

    return (
        (code >= 0x1f000 && code <= 0x1f9ff) || // Emoticons, symbols, pictographs, transport
        (code >= 0x2300 && code <= 0x27bf) || // Misc technical, symbols, dingbats
        (code >= 0x2600 && code <= 0x26ff) || // Misc symbols
        (code >= 0x2b05 && code <= 0x2b55) // Arrows, shapes
    );
};

export const isValidColonPosition = (lastColonIndex: number, value: string) => {
    if (lastColonIndex === 0) {
        return true;
    }

    const getCharBeforeColon = () => {
        const charCode = value.charCodeAt(lastColonIndex - 1);

        if (charCode >= 0xdc00 && charCode <= 0xdfff) {
            if (lastColonIndex >= 2) {
                return value.substring(lastColonIndex - 2, lastColonIndex);
            }
        }

        return value[lastColonIndex - 1];
    };

    const charBeforeColon = getCharBeforeColon();

    return charBeforeColon === ' ' || isEmoji(charBeforeColon);
};

export const getSummaryOptions = (i18next: i18n) =>
    [
        SummaryOption.Today,
        SummaryOption.Yesterday,
        SummaryOption.Last7Days,
        SummaryOption.Last14Days,
        SummaryOption.Last30Days,
    ].map((option) => ({
        key: option,
        label: i18next.t(`chat.generateSummary.options.${option}`),
    }));

export const getSummaryValue = (
    option: SummaryOption,
): SummarizeOptionValue => {
    if (option === SummaryOption.Unread) {
        return { from: null, to: null, read: 'false' };
    }

    const from = new Date();
    from.setHours(0, 0, 0, 0);

    const daysOffsetMap: Record<string, number> = {
        [SummaryOption.Yesterday]: 1,
        [SummaryOption.Last7Days]: 7,
        [SummaryOption.Last14Days]: 14,
        [SummaryOption.Last30Days]: 30,
    };

    if (option in daysOffsetMap) {
        from.setDate(from.getDate() - daysOffsetMap[option]);
    }

    if (option === SummaryOption.Yesterday) {
        const to = new Date(from);
        to.setHours(23, 59, 59, 999);
        return {
            from: from.toISOString(),
            to: to.toISOString(),
            read: null,
        };
    }

    return { from: from.toISOString(), to: null, read: null };
};
